import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Form } from '@edx/paragon';
import { ReactComponent as RadioIcon } from '../../profile/assets/radio-button.svg';
import { ReactComponent as RadioEmptyIcon } from '../../profile/assets/radio-button-empty.svg';

import messages from './LearningGoals.messages';

// Components
import FormControls from './elements/FormControls';
import EditableItemHeader from './elements/EditableItemHeader';
import EmptyContent from './elements/EmptyContent';
import SwitchContent from './elements/SwitchContent';
import CustomSelect from '../components/CustomSelect/CustomSelect';

const LearningGoals = ({
  formId,
  learningGoal,
  saveState,
  error,
  intl,
  goals,
  changeHandler,
  submitHandler,
  closeHandler,
  openHandler,
  changeData,
  saveData,
  onSaveComplete,
  editMode: initialEditMode,
}) => {
  const [editMode, setEditMode] = useState(changeData ? 'editing' : initialEditMode);
  const [selectedValue, setSelectedValue] = useState(learningGoal?.id?.toString() || '');

  useEffect(() => {
    setSelectedValue(learningGoal?.id?.toString() || '');
  }, [learningGoal]);

  useEffect(() => {
    if (changeData) {
      setEditMode('editing');
    } else {
      handleClose();
    }
  }, [changeData]);

  useEffect(() => {
    if (saveData) {
      handleSubmit();
    }
  }, [saveData]);

  const handleChange = value => {
    setSelectedValue(value);

    // Find the complete goal object from the goals array
    const selectedGoal = goals.find(goal => goal.id.toString() === value);
    // Send as an array as the API expects learningGoals array
    const goalsArray = selectedGoal ? [selectedGoal] : [];

    changeHandler(formId, goalsArray);
  };

  const handleSubmit = e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitHandler(formId);
    if (onSaveComplete) {
      onSaveComplete();
    }
  };

  const handleClose = () => {
    closeHandler(formId);
    setEditMode('static');
  };

  const handleOpen = () => {
    openHandler(formId);
    setEditMode('editing');
  };

  const goalOptions = [
    {
      value: '',
      label: '——',
      displayLabel: intl.formatMessage(messages['profile.learningGoals.option.default']),
    },
    ...goals.map(({ id, name }) => ({
      value: id.toString(),
      label: name,
      displayLabel: name,
    })),
  ];

  const renderOption = (option, currentValue) => {
    const isSelected = option.value === currentValue;
    const Icon = isSelected ? RadioIcon : RadioEmptyIcon;

    return (
      <>
        <Icon className="tw-w-4 tw-h-4 tw-min-w-4 tw-min-h-4" />
        <span className={option.value === '' ? 'tw-text-neutral-500' : ''}>{option.label}</span>
      </>
    );
  };

  return (
    <SwitchContent
      expression={editMode}
      cases={{
        editing: (
          <div role="dialog" aria-labelledby={`${formId}-label`}>
            <form onSubmit={handleSubmit}>
              <Form.Group controlId={formId} isInvalid={error !== null} className="tw-mb-0">
                <p className="!tw-mb-2 tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[100%]">
                  {intl.formatMessage(messages['profile.learningGoals.label'])}
                </p>
                <p className="tw-text-[14px] tw-text-placeholder tw-font-medium !tw-mb-[14px] tw-leading-[120%]">
                  {intl.formatMessage(messages['profile.learningGoals.placeholder'])}
                </p>
                <CustomSelect
                  options={goalOptions}
                  value={selectedValue}
                  onChange={handleChange}
                  placeholder={intl.formatMessage(messages['profile.learningGoals.option.default'])}
                  renderOption={renderOption}
                  getOptionLabel={option => option.displayLabel}
                  getOptionValue={option => option.value}
                  enableSearch={false}
                />
                {error !== null && (
                  <Form.Control.Feedback hasIcon={false}>{error}</Form.Control.Feedback>
                )}
              </Form.Group>
              <div className="tw-hidden">
                <FormControls
                  saveState={saveState}
                  cancelHandler={handleClose}
                  changeHandler={handleChange}
                  saveData={saveData}
                  visibilityId={formId}
                />
              </div>
            </form>
          </div>
        ),
        editable: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.learningGoals.label'])}
              showEditButton
              onClickEdit={handleOpen}
            />
            <p data-hj-suppress className="h5">
              {learningGoal?.name}
            </p>
          </>
        ),
        empty: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.learningGoals.label'])}
            />
            <EmptyContent onClick={handleOpen}>
              {intl.formatMessage(messages['profile.learningGoals.empty'])}
            </EmptyContent>
          </>
        ),
        static: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.learningGoals.label'])}
            />
            <p
              data-hj-suppress
              className="tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[150%]"
            >
              {learningGoal?.name || (
                <span className="tw-text-[14px] tw-text-placeholder tw-font-medium">
                  {intl.formatMessage(messages['profile.learningGoals.placeholder'])}
                </span>
              )}
            </p>
          </>
        ),
      }}
    />
  );
};

LearningGoals.propTypes = {
  formId: PropTypes.string.isRequired,
  learningGoal: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,
  error: PropTypes.string,
  goals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      is_active: PropTypes.bool,
    }),
  ).isRequired,
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  changeData: PropTypes.bool,
  saveData: PropTypes.bool,
  onSaveComplete: PropTypes.func,
};

LearningGoals.defaultProps = {
  editMode: 'static',
  saveState: null,
  learningGoal: { id: '', name: '' },
  error: null,
  changeData: false,
  saveData: false,
  onSaveComplete: null,
};

export default connect(null, {})(injectIntl(LearningGoals));
